import { getMapStopArrivalTimePillPresentation } from "../../../../theme/DelayStatusColors";
import { hexToRgb, toReadableHex } from "../../../../tools/ColorTools";
import { getRelativeLuminance } from "../../vehicleInfoChrome/vehicleInfoChrome.tools";
import {
    resolveStopLabelTransitRoutesForDisplay,
    STOP_LABEL_ROUTE_COLUMNS,
    STOP_LABEL_ROUTE_MAX_ROWS,
} from "../../../../tools/transitRouteDisplay.tools";
import type { GtfsRoute } from "../../../../types/Gtfs.types";
import {
    applyStopLabelCorrespondenceBadgeFixedWidth,
    STOP_LABEL_CONTENT_ROW_CLASS,
    STOP_LABEL_CORRESPONDENCE_BADGE_WIDTH_CLASS,
    STOP_LABEL_NAME_CLASS,
    STOP_LABEL_NAME_ONLY_CLASS,
} from "./stopLabel.placement";
import type { StopLabelData } from "./stopLabel.types";

/** Base Tailwind classes for arrival-time pills (radius supplied by the parent shell). */
const STOP_LABEL_ARRIVAL_TIME_PILL_BASE_CLASS =
    "inline-flex shrink-0 items-center justify-center border box-border px-1 py-px text-[9px] font-bold leading-none min-h-[14px]";

/** Vertical stack wrapper when correspondence badges are shown. */
const STOP_LABEL_TWO_LINE_STACK_CLASS = "flex flex-col w-full gap-1";

/** Thin separator between stop name row and correspondence badges. */
const STOP_LABEL_CORRESPONDENCE_DIVIDER_CLASS =
    "h-px w-full shrink-0 bg-slate-300/70 dark:bg-slate-500/50";

/** Vertical stack of correspondence badge rows (each row is horizontal). */
const STOP_LABEL_CORRESPONDENCE_ROWS_CLASS = "flex flex-col gap-0.5 w-full";

/** Single horizontal row of correspondence badges (up to four). */
const STOP_LABEL_CORRESPONDENCE_ROW_CLASS = "flex flex-row gap-0.5";

/** Shared compact route badge classes for map stop labels. */
const STOP_LABEL_ROUTE_BADGE_BASE_CLASS =
    "inline-flex flex-none box-border items-center justify-center overflow-hidden truncate rounded-full border font-bold text-[9px] leading-none text-center py-px px-0 min-h-[12px]";

/** White border around correspondence badges. */
const STOP_LABEL_ROUTE_BADGE_BORDER_COLOR = "#ffffff";

/** Ellipsis pill styling aligned with search autocomplete. */
const STOP_LABEL_ELLIPSIS_BACKGROUND = "#cecece";
const STOP_LABEL_ELLIPSIS_TEXT_COLOR = "#000000";

/** Default route badge background when the GTFS route color is missing. */
const STOP_LABEL_DEFAULT_ROUTE_COLOR = "13d39c";

/** Dark-side luminance threshold shared with the Dynamic Island badge logic. */
const STOP_LABEL_BADGE_DARK_LUMINANCE = 0.5;

/** Minimum luminance gap required to trust a feed-provided badge text color. */
const STOP_LABEL_BADGE_MIN_LUMINANCE_GAP = 0.25;

/** Accepted badge text color forms (`#RGB`, `#RRGGBB`, with or without `#`). */
const BADGE_HEX_COLOR_PATTERN = /^#?(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

/**
 * Resolves the text color of a route badge.
 *
 * Feed-provided `textColor` is kept only when it sits on the opposite luminance side
 * of the badge background with a real gap (some feeds ship unreadable pairs, e.g.
 * TPG `#000000` on `#EC619F`). Otherwise the color is computed like the Dynamic
 * Island badge: white on dark backgrounds, black on light ones.
 *
 * @param backgroundHex Badge background color (any GTFS hex form).
 * @param feedTextHex Optional feed `route_text_color` value; empty/invalid is treated as absent.
 * @returns `#RRGGBB` text color safe for the badge.
 */
function resolveStopLabelBadgeTextColor( backgroundHex: string, feedTextHex: string | undefined ): string {
    const backgroundRgb = hexToRgb( backgroundHex );
    const backgroundLuminance = getRelativeLuminance( backgroundRgb.r, backgroundRgb.g, backgroundRgb.b );
    const backgroundIsDark = backgroundLuminance <= STOP_LABEL_BADGE_DARK_LUMINANCE;
    const fallbackTextHex = backgroundIsDark ? "#ffffff" : "#000000";

    const trimmedTextHex = feedTextHex?.trim() ?? "";

    if ( !BADGE_HEX_COLOR_PATTERN.test( trimmedTextHex ) ) {
        return fallbackTextHex;
    }

    const textRgb = hexToRgb( trimmedTextHex );
    const textLuminance = getRelativeLuminance( textRgb.r, textRgb.g, textRgb.b );
    const textIsDark = textLuminance <= STOP_LABEL_BADGE_DARK_LUMINANCE;

    if ( textIsDark !== backgroundIsDark &&
      Math.abs( textLuminance - backgroundLuminance ) >= STOP_LABEL_BADGE_MIN_LUMINANCE_GAP ) {
        return toReadableHex( trimmedTextHex );
    }

    return fallbackTextHex;
}

/**
 * Creates the left-aligned, ellipsis-capable stop name element.
 *
 * @param name Visible stop name.
 * @param className Tailwind classes for the name cell.
 * @returns Span element for the label row.
 */
function createStopLabelNameElement( name: string, className: string ): HTMLSpanElement {
    const nameEl = document.createElement( "span" );
    nameEl.className = className;
    nameEl.textContent = name;
    nameEl.title = name;

    return nameEl;
}

/**
 * Appends the first row (stop name and optional arrival-time pill) to a row container.
 *
 * @param rowEl Horizontal flex row element.
 * @param data Stop label presentation data.
 * @param isDarkUi Whether dark theme is currently active.
 * @param borderRadiusClass Tailwind radius utility for the arrival-time pill.
 */
function appendStopLabelPrimaryRow(
    rowEl: HTMLDivElement,
    data: StopLabelData,
    isDarkUi: boolean,
    borderRadiusClass: string,
): void {
    rowEl.className = STOP_LABEL_CONTENT_ROW_CLASS;

    if ( data.arrivalTime === null ) {
        rowEl.appendChild( createStopLabelNameElement( data.name, STOP_LABEL_NAME_ONLY_CLASS ) );

        return;
    }

    rowEl.appendChild( createStopLabelNameElement( data.name, STOP_LABEL_NAME_CLASS ) );

    const pillPresentation = getMapStopArrivalTimePillPresentation(
        isDarkUi,
        data.delaySec,
        data.hasRealtimeDelay,
        data.applyDelayColoring,
    );
    const arrivalTimeEl = document.createElement( "span" );
    arrivalTimeEl.className = `${ STOP_LABEL_ARRIVAL_TIME_PILL_BASE_CLASS } ${ borderRadiusClass }`;
    arrivalTimeEl.textContent = data.arrivalTime;
    arrivalTimeEl.style.backgroundColor = pillPresentation.backgroundColor;
    arrivalTimeEl.style.color = pillPresentation.textColor;
    arrivalTimeEl.style.borderColor = pillPresentation.borderColor;
    rowEl.appendChild( arrivalTimeEl );
}

/**
 * Creates a compact route badge element for map stop labels.
 *
 * @param route GTFS route descriptor.
 * @returns Styled badge span.
 */
function createStopLabelRouteBadgeElement( route: GtfsRoute ): HTMLSpanElement {
    const badgeEl = document.createElement( "span" );
    badgeEl.className = `${ STOP_LABEL_ROUTE_BADGE_BASE_CLASS } ${ STOP_LABEL_CORRESPONDENCE_BADGE_WIDTH_CLASS }`;
    badgeEl.textContent = route.name;
    badgeEl.title = route.name;
    const backgroundHex = route.color ?? STOP_LABEL_DEFAULT_ROUTE_COLOR;
    badgeEl.style.backgroundColor = toReadableHex( backgroundHex );
    badgeEl.style.color = resolveStopLabelBadgeTextColor( backgroundHex, route.textColor );
    badgeEl.style.borderColor = STOP_LABEL_ROUTE_BADGE_BORDER_COLOR;
    applyStopLabelCorrespondenceBadgeFixedWidth( badgeEl );

    return badgeEl;
}

/**
 * Creates the ellipsis pill shown when more routes exist than the display limit.
 *
 * @returns Styled ellipsis badge span.
 */
function createStopLabelEllipsisBadgeElement(): HTMLSpanElement {
    const ellipsisEl = document.createElement( "span" );
    ellipsisEl.className = `${ STOP_LABEL_ROUTE_BADGE_BASE_CLASS } ${ STOP_LABEL_CORRESPONDENCE_BADGE_WIDTH_CLASS }`;
    ellipsisEl.textContent = "...";
    ellipsisEl.style.backgroundColor = STOP_LABEL_ELLIPSIS_BACKGROUND;
    ellipsisEl.style.color = STOP_LABEL_ELLIPSIS_TEXT_COLOR;
    ellipsisEl.style.borderColor = STOP_LABEL_ROUTE_BADGE_BORDER_COLOR;
    applyStopLabelCorrespondenceBadgeFixedWidth( ellipsisEl );

    return ellipsisEl;
}

/**
 * Appends correspondence badge rows below the primary row divider.
 *
 * Fills each horizontal row left-to-right (up to four badges) before wrapping
 * to the next line (at most two lines).
 *
 * @param parent Stack container receiving the divider and badge rows.
 * @param routes Full correspondence route list for the stop.
 */
function appendStopLabelCorrespondenceRow(
    parent: HTMLDivElement,
    routes: readonly GtfsRoute[],
): void {
    const dividerEl = document.createElement( "div" );
    dividerEl.className = STOP_LABEL_CORRESPONDENCE_DIVIDER_CLASS;
    parent.appendChild( dividerEl );

    const { displayedRoutes, showEllipsis } = resolveStopLabelTransitRoutesForDisplay( routes );

    const rowsEl = document.createElement( "div" );
    rowsEl.className = STOP_LABEL_CORRESPONDENCE_ROWS_CLASS;

    const maxSlots = STOP_LABEL_ROUTE_COLUMNS * STOP_LABEL_ROUTE_MAX_ROWS;
    const slotCount = Math.min(
        displayedRoutes.length + ( showEllipsis ? 1 : 0 ),
        maxSlots,
    );

    for ( let rowIndex = 0; rowIndex < STOP_LABEL_ROUTE_MAX_ROWS; rowIndex += 1 ) {
        const rowStart = rowIndex * STOP_LABEL_ROUTE_COLUMNS;
        if ( rowStart >= slotCount ) {
            break;
        }

        const rowEnd = Math.min( rowStart + STOP_LABEL_ROUTE_COLUMNS, slotCount );
        const rowEl = document.createElement( "div" );
        rowEl.className = STOP_LABEL_CORRESPONDENCE_ROW_CLASS;

        for ( let slotIndex = rowStart; slotIndex < rowEnd; slotIndex += 1 ) {
            const isEllipsisSlot = showEllipsis && slotIndex === displayedRoutes.length;
            rowEl.appendChild( isEllipsisSlot
                ? createStopLabelEllipsisBadgeElement()
                : createStopLabelRouteBadgeElement( displayedRoutes[slotIndex] ) );
        }

        rowsEl.appendChild( rowEl );
    }

    parent.appendChild( rowsEl );
}

/**
 * Builds a plain-text fallback label from stop label data.
 *
 * @param data Presentation-ready stop label data.
 * @returns `"StopName HH:MM"` or just `"StopName"`.
 */
export function buildStopLabelLine1( data: StopLabelData ): string {
    if ( data.arrivalTime !== null ) {
        return `${ data.name } ${ data.arrivalTime }`;
    }

    return data.name;
}

/**
 * Populates a container with stop label content.
 *
 * Single-line when {@link StopLabelData.correspondenceRoutes} is empty; otherwise
 * stacks the name row, a gray divider, and compact correspondence badges.
 *
 * @param container Parent element to populate.
 * @param data Stop label presentation data.
 * @param isDarkUi Whether dark theme is currently active.
 * @param borderRadiusClass Tailwind radius utility for the arrival-time pill.
 */
export function appendStopLabelContent(
    container: HTMLDivElement,
    data: StopLabelData,
    isDarkUi: boolean,
    borderRadiusClass: string,
): void {
    const hasCorrespondences = data.correspondenceRoutes.length > 0;

    if ( !hasCorrespondences ) {
        container.className = `${ container.className } ${ STOP_LABEL_CONTENT_ROW_CLASS }`.trim();
        appendStopLabelPrimaryRow( container, data, isDarkUi, borderRadiusClass );

        return;
    }

    container.className = `${ container.className } ${ STOP_LABEL_TWO_LINE_STACK_CLASS }`.trim();

    const primaryRowEl = document.createElement( "div" );
    appendStopLabelPrimaryRow( primaryRowEl, data, isDarkUi, borderRadiusClass );
    container.appendChild( primaryRowEl );

    appendStopLabelCorrespondenceRow( container, data.correspondenceRoutes );
}
